/**
 * Fila de trabalhadores e a decisão do Operations Manager.
 *
 * O passo 13 do plano: abrir o perfil, ver o que o agente recolheu, e
 * APROVAR / DEVOLVER / REJEITAR.
 *
 * Devolver e rejeitar exigem motivo — o servidor recusa sem ele, e é bem
 * assim: o agente que recebe "devolvido" sem explicação não sabe o que ir
 * corrigir, e volta ao terreno às cegas.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Loader2, Search, MapPin, User, FileText, ChevronRight, X, Check, Undo2, Ban, KeyRound,
  ShieldCheck, Plus, AlertTriangle,
} from 'lucide-react';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';

const FASES = {
  pending:  { txt: 'Candidato',            cls: 'bg-slate-700 text-slate-300' },
  verified: { txt: 'Documentação validada', cls: 'bg-blue-500/15 text-blue-400' },
  approved: { txt: 'Entrevista aprovada',  cls: 'bg-purple-500/15 text-purple-400' },
  trained:  { txt: 'Formação concluída',   cls: 'bg-cyan-500/15 text-cyan-400' },
  active:   { txt: 'Activo',               cls: 'bg-green-500/15 text-green-400' },
  returned: { txt: 'Devolvido',            cls: 'bg-amber-500/15 text-amber-400' },
  rejected: { txt: 'Rejeitado',            cls: 'bg-red-500/15 text-red-400' },
};

/** A fase seguinte no percurso, e como se chama o botão que lá leva. */
const AVANCO = {
  pending:  { fase: 'verified', rotulo: 'Validar documentação' },
  verified: { fase: 'approved', rotulo: 'Aprovar entrevista' },
  approved: { fase: 'trained',  rotulo: 'Marcar formação concluída' },
  trained:  { fase: 'active',   rotulo: 'Activar' },
  returned: { fase: 'verified', rotulo: 'Validar documentação' },
};

const dt = (v) => (v ? new Date(v).toLocaleString('pt-PT') : '—');
const kz = (v) => (v == null ? '—' : `${Number(v).toLocaleString('pt-PT')} Kz`);

const OperationsPipeline = () => {
  const [params, setParams] = useSearchParams();
  const { success, error } = useToast();
  const [lista, setLista] = useState(null);
  const [zonas, setZonas] = useState([]);
  const [procura, setProcura] = useState('');
  const [aberto, setAberto] = useState(null);
  const [percurso, setPercurso] = useState([]);
  const [avaliacao, setAvaliacao] = useState(null);   // entrevista + formação
  const [motivo, setMotivo] = useState('');
  const [accao, setAccao] = useState(null);   // 'returned' | 'rejected'
  const [busy, setBusy] = useState(false);
  const [acessoInicial, setAcessoInicial] = useState(null);
  const [capacidades, setCapacidades] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [novaCap, setNovaCap] = useState(null);   // { categoria, tecto_kz }
  const [tectos, setTectos] = useState({});       // rascunho do tecto por capacidade

  const fase = params.get('fase') || '';
  const zona = params.get('zona') || '';

  const carregar = useCallback(() => {
    setLista(null);
    const p = {};
    if (fase) p.fase = fase;
    if (zona) p.zona = zona;
    if (procura.trim()) p.q = procura.trim();
    api.get('/operations/my-workers', { params: p })
      .then((r) => setLista(r.data || []))
      .catch(() => setLista([]));
  }, [fase, zona, procura]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    api.get('/operations/zones').then((r) => setZonas(r.data || [])).catch(() => {});
    api.get('/operations/categorias-obra').then((r) => setCategorias(r.data || [])).catch(() => {});
  }, []);

  const carregarCapacidades = useCallback(async (userId) => {
    try {
      const { data } = await api.get(`/operations/workers/${userId}/capacidades`);
      setCapacidades(Array.isArray(data) ? data : []);
      setTectos(Object.fromEntries((data || []).map((c) => [c.capacidade_id, c.valor_maximo_kz ?? ''])));
    } catch { setCapacidades([]); }
  }, []);

  const abrir = async (w) => {
    setAberto(w); setMotivo(''); setAccao(null); setPercurso([]); setAvaliacao(null); setAcessoInicial(null);
    setCapacidades(null); setNovaCap(null);
    carregarCapacidades(w.user_id);
    try {
      const { data } = await api.get(`/operations/workers/${w.user_id}/pipeline`);
      setPercurso(Array.isArray(data) ? data : []);
    } catch { setPercurso([]); }
    // Activar alguém sem ver a pontuação da entrevista nem a formação é
    // decidir às cegas — é exactamente isto que a gestão precisa de ler.
    try {
      const [e, f] = await Promise.all([
        api.get(`/operations/workers/${w.user_id}/interview`),
        api.get(`/operations/workers/${w.user_id}/training`),
      ]);
      setAvaliacao({ entrevista: (e.data || [])[0] || null, formacao: f.data || null });
    } catch { setAvaliacao(null); }
  };

  const mover = async (novaFase, comMotivo) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/operations/workers/${aberto.user_id}/pipeline`, {
        fase: novaFase, motivo: comMotivo || undefined,
      });
      success(`${aberto.worker_code}: ${FASES[data.para]?.txt || data.para}.`);
      if (data.acesso_inicial) {
        setAcessoInicial(data.acesso_inicial);
        setAberto((w) => ({ ...w, pipeline_status: 'active', tem_acesso: true }));
      } else {
        setAberto(null);
      }
      carregar();
    } catch (e) {
      error(e.response?.data?.message || 'Não foi possível mover a fase.');
    } finally { setBusy(false); }
  };

  const gravarCapacidade = async (cap, verificado) => {
    setBusy(true);
    try {
      await api.patch(`/operations/capacidades/${cap.capacidade_id}`, {
        verificado, tecto_kz: tectos[cap.capacidade_id],
      });
      success(verificado
        ? `${cap.categoria_efectiva} verificada até ${kz(tectos[cap.capacidade_id])}.`
        : `${cap.categoria_efectiva} deixou de estar verificada.`);
      carregarCapacidades(aberto.user_id);
    } catch (e) {
      error(e.response?.data?.message || 'Não foi possível gravar a capacidade.');
    } finally { setBusy(false); }
  };

  const criarCapacidade = async () => {
    setBusy(true);
    try {
      await api.post(`/operations/workers/${aberto.user_id}/capacidades`, novaCap);
      success('Capacidade acrescentada. Falta verificá-la.');
      setNovaCap(null);
      carregarCapacidades(aberto.user_id);
    } catch (e) {
      error(e.response?.data?.message || 'Não foi possível acrescentar.');
    } finally { setBusy(false); }
  };

  const filtrar = (chave, valor) => {
    const p = new URLSearchParams(params);
    if (valor) p.set(chave, valor); else p.delete(chave);
    setParams(p);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-white">Profissionais</h1>
        <p className="text-sm text-slate-400 mt-1">Percurso de recrutamento.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={procura}
            onChange={(e) => setProcura(e.target.value)}
            placeholder="Nome, telefone ou código"
            aria-label="Procurar profissional"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-sm outline-none focus:border-blue-500"
          />
        </div>
        <select value={fase} onChange={(e) => filtrar('fase', e.target.value)} aria-label="Filtrar por fase"
          className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-sm">
          <option value="">Todas as fases</option>
          {Object.entries(FASES).map(([k, v]) => <option key={k} value={k}>{v.txt}</option>)}
        </select>
        <select value={zona} onChange={(e) => filtrar('zona', e.target.value)} aria-label="Filtrar por zona"
          className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 text-sm">
          <option value="">Todas as zonas</option>
          {zonas.map((z) => <option key={z.zone_id} value={z.zone_id}>{z.nome}</option>)}
        </select>
      </div>

      {lista === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
      ) : lista.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <User className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium text-slate-400">Nenhum profissional nesta vista</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {lista.map((w) => {
            const f = FASES[w.pipeline_status] || FASES.pending;
            return (
              <li key={w.user_id}>
                <button onClick={() => abrir(w)}
                  className="w-full text-left p-3 rounded-xl border border-slate-800 bg-slate-900/50 hover:bg-slate-800/60">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-100 truncate">{w.full_name}</div>
                      <div className="text-xs text-slate-500 font-mono">{w.worker_code}</div>
                    </div>
                    <span className={`shrink-0 px-2 py-1 rounded-md text-[11px] font-semibold ${f.cls}`}>{f.txt}</span>
                    <ChevronRight className="w-4 h-4 text-slate-600 shrink-0 mt-1" />
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 flex-wrap">
                    {w.profession && <span>{w.profession}</span>}
                    {w.zona_nome && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{w.zona_nome}</span>}
                    <span className={`flex items-center gap-1 ${w.tem_documentos ? 'text-slate-400' : 'text-amber-500'}`}>
                      <FileText className="w-3 h-3" />
                      {w.tem_documentos ? 'Com documentos' : 'Sem documentos'}
                    </span>
                    {w.agente && <span className="ml-auto">{w.agente}</span>}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* ── Perfil e decisão ── */}
      {aberto && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center sm:p-4"
          onClick={() => !busy && setAberto(null)}>
          <div className="bg-slate-900 border border-slate-700 w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl
                          max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-white">{aberto.full_name}</h2>
                <p className="text-sm text-blue-400 font-mono">{aberto.worker_code}</p>
              </div>
              <button onClick={() => setAberto(null)} aria-label="Fechar"
                className="grid place-items-center w-11 h-11 rounded-xl bg-slate-800 text-slate-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            <dl className="rounded-xl bg-slate-800/50 divide-y divide-slate-800 mb-4">
              {[
                ['Estado', FASES[aberto.pipeline_status]?.txt],
                ['Telefone', aberto.phone_number],
                ['Profissão', aberto.profession || '—'],
                ['Especialidades', (aberto.specialties || []).join(', ') || '—'],
                ['Experiência', aberto.years_experience ? `${aberto.years_experience} anos` : '—'],
                ['Zona', aberto.zona_nome || '—'],
                ['Documentos', aberto.tem_documentos
                  ? `Recolhidos${aberto.bi_number ? ` · BI ${aberto.bi_number}` : ''}`
                  : 'Em falta'],
                ['Cadastrado por', aberto.agente || '—'],
                ['Cadastrado em', dt(aberto.recruited_at)],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-3 px-3 py-2 text-sm">
                  <dt className="text-slate-500 w-32 shrink-0">{k}</dt>
                  <dd className="flex-1 min-w-0 break-words text-slate-200">{v}</dd>
                </div>
              ))}
            </dl>

            {avaliacao?.entrevista && (
              <div className="mb-4 p-3 rounded-xl border border-slate-800 bg-slate-950">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold tabular-nums text-slate-100">
                    {avaliacao.entrevista.pontuacao}<span className="text-sm text-slate-600">/100</span>
                  </span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-md
                    ${avaliacao.entrevista.aprovado ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>
                    {avaliacao.entrevista.aprovado ? 'Aprovado' : 'Reprovado'}
                  </span>
                  <span className="text-xs text-slate-600 ml-auto">
                    {avaliacao.entrevista.avaliador || '—'} · {dt(avaliacao.entrevista.created_at)}
                  </span>
                </div>
                {avaliacao.entrevista.eliminado_por && (
                  <p className="text-xs text-amber-400 mt-1">Eliminatória: {avaliacao.entrevista.eliminado_por}</p>
                )}
                {avaliacao.entrevista.notas && (
                  <p className="text-xs text-slate-400 mt-1.5">{avaliacao.entrevista.notas}</p>
                )}
                {/* As respostas que mais pesaram contra — poupa abrir o detalhe todo. */}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(avaliacao.entrevista.detalhe || [])
                    .filter((d) => d.pontos < d.peso * 0.6)
                    .map((d) => (
                      <span key={d.id} className="text-[11px] px-2 py-0.5 rounded bg-slate-800 text-slate-400">
                        {d.rotulo} · {d.pontos}/{d.peso}
                      </span>
                    ))}
                </div>
                {avaliacao.formacao && (
                  <p className="text-xs text-slate-500 mt-2">
                    Formação: {avaliacao.formacao.concluidos}/{avaliacao.formacao.obrigatorios} módulos
                    {avaliacao.formacao.completo ? ' — completa' : ''}
                  </p>
                )}
              </div>
            )}

            {/* ── Capacidades ──
                Sem uma capacidade verificada, o trabalhador não é convidado
                para concurso nenhum. É por isso que isto está aqui e não numa
                página à parte: é o último portão antes de ele poder trabalhar
                em obra. */}
            <div className="mb-4">
              <h3 className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                <ShieldCheck className="w-3.5 h-3.5" /> Capacidades para obras
              </h3>

              {capacidades === null ? (
                <div className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-600" /></div>
              ) : (
                <>
                  {capacidades.length === 0 && !novaCap && (
                    <p className="text-xs text-slate-500 mb-2">
                      Nenhuma declarada — este profissional não recebe concursos.
                    </p>
                  )}
                  {capacidades.length > 0 && !capacidades.some((c) => c.verificado) && (
                    <p className="flex items-start gap-1.5 text-xs text-amber-400 mb-2">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                      Nenhuma verificada — não vai aparecer em concursos.
                    </p>
                  )}

                  <ul className="space-y-2">
                    {capacidades.map((c) => (
                      <li key={c.capacidade_id} className="p-3 rounded-xl border border-slate-800 bg-slate-950">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-slate-200 text-sm">{c.categoria_efectiva || '—'}</span>
                          <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold ${
                            c.verificado ? 'bg-green-500/15 text-green-400' : 'bg-slate-700 text-slate-400'}`}>
                            {c.verificado ? 'Verificada' : 'Por verificar'}
                          </span>
                          {c.verificado && (
                            <span className="ml-auto text-xs text-slate-500 tabular-nums">
                              até {kz(c.valor_maximo_kz)}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2 items-end">
                          <div className="flex-1">
                            <label htmlFor={`tecto-${c.capacidade_id}`}
                              className="block text-[11px] text-slate-500 mb-1">
                              Tecto de obra (Kz)
                            </label>
                            <input
                              id={`tecto-${c.capacidade_id}`} type="number" min="1" inputMode="numeric"
                              value={tectos[c.capacidade_id] ?? ''}
                              onChange={(e) => setTectos((t) => ({ ...t, [c.capacidade_id]: e.target.value }))}
                              placeholder="Ex.: 1000000"
                              className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700
                                         text-slate-100 text-sm tabular-nums outline-none focus:border-blue-500"
                            />
                          </div>
                          <button
                            onClick={() => gravarCapacidade(c, !c.verificado)}
                            disabled={busy || (!c.verificado && !String(tectos[c.capacidade_id] || '').trim())}
                            className={`h-[38px] px-4 rounded-lg text-sm font-semibold disabled:opacity-40 ${
                              c.verificado ? 'bg-slate-800 text-amber-400' : 'bg-green-600 text-white'}`}
                          >
                            {c.verificado ? 'Retirar' : 'Verificar'}
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>

                  {novaCap ? (
                    <div className="mt-2 p-3 rounded-xl border border-slate-700 bg-slate-950">
                      <label htmlFor="nova-cat" className="block text-[11px] text-slate-500 mb-1">
                        Categoria de obra
                      </label>
                      <select
                        id="nova-cat" value={novaCap.categoria}
                        onChange={(e) => setNovaCap((n) => ({ ...n, categoria: e.target.value }))}
                        className="w-full px-3 py-2 mb-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm"
                      >
                        <option value="">Escolher…</option>
                        {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <div className="flex gap-2">
                        <button onClick={criarCapacidade} disabled={busy || !novaCap.categoria}
                          className="flex-1 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-40">
                          Acrescentar
                        </button>
                        <button onClick={() => setNovaCap(null)} disabled={busy}
                          className="px-4 py-2.5 rounded-lg bg-slate-800 text-slate-300 text-sm font-medium">
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setNovaCap({ categoria: '' })}
                      className="mt-2 flex items-center gap-1.5 text-xs text-blue-400 font-medium py-2">
                      <Plus className="w-3.5 h-3.5" /> Acrescentar capacidade
                    </button>
                  )}
                </>
              )}
            </div>

            {percurso.length > 0 && (
              <div className="mb-4">
                <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Percurso</h3>
                <ol className="space-y-1.5 text-xs">
                  {percurso.map((h, i) => (
                    <li key={i} className="flex gap-2 text-slate-400 flex-wrap">
                      <span className="text-slate-600 tabular-nums">{dt(h.created_at)}</span>
                      <span className="text-slate-300">{FASES[h.para_fase]?.txt || h.para_fase}</span>
                      {h.actor_nome && <span className="text-slate-600">· {h.actor_nome}</span>}
                      {h.motivo && <span className="w-full text-amber-400/80 pl-1">↳ {h.motivo}</span>}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Motivo obrigatório para devolver ou rejeitar */}
            {accao && (
              <div className="mb-3">
                <label htmlFor="motivo" className="block text-sm font-medium text-slate-300 mb-1.5">
                  Motivo <span className="text-red-400">*</span>
                  <span className="text-slate-500 font-normal"> — o agente vai ler isto</span>
                </label>
                <textarea
                  id="motivo" rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ex.: Fotografia do verso do documento ilegível."
                  className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-slate-100 text-sm outline-none focus:border-blue-500"
                />
              </div>
            )}

            <div className="flex flex-col gap-2">
              {acessoInicial && (
                <div className="mb-2 rounded-xl border border-green-500/40 bg-green-500/10 p-4">
                  <div className="flex items-center gap-2 text-green-300 font-semibold mb-3">
                    <KeyRound className="w-5 h-5" /> Acesso inicial criado
                  </div>
                  <p className="text-xs text-slate-300 mb-3">
                    Entregue estes dados ao profissional. A palavra-passe só é mostrada agora.
                  </p>
                  <dl className="space-y-2 text-sm">
                    <div><dt className="text-slate-500">Worker ID</dt><dd className="font-mono text-white select-all">{acessoInicial.identificador}</dd></div>
                    <div><dt className="text-slate-500">Palavra-passe temporária</dt><dd className="font-mono text-white text-lg select-all">{acessoInicial.palavra_passe_temporaria}</dd></div>
                  </dl>
                  <p className="text-xs text-amber-300 mt-3">Peça ao profissional para a alterar em Definições depois do primeiro login.</p>
                </div>
              )}
              {accao ? (
                <>
                  <button
                    onClick={() => mover(accao, motivo)}
                    disabled={busy || !motivo.trim()}
                    className={`py-3.5 rounded-xl font-semibold disabled:opacity-40
                      ${accao === 'rejected' ? 'bg-red-600' : 'bg-amber-600'}`}
                  >
                    {busy ? 'A gravar…' : accao === 'rejected' ? 'Confirmar rejeição' : 'Confirmar devolução'}
                  </button>
                  <button onClick={() => { setAccao(null); setMotivo(''); }} disabled={busy}
                    className="py-3 rounded-xl bg-slate-800 text-slate-300 font-medium">
                    Cancelar
                  </button>
                </>
              ) : (
                <>
                  {AVANCO[aberto.pipeline_status] && (
                    <button
                      onClick={() => mover(AVANCO[aberto.pipeline_status].fase)}
                      disabled={busy}
                      className="flex items-center justify-center gap-2 py-3.5 rounded-xl bg-green-600 font-semibold disabled:opacity-50"
                    >
                      {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                      {AVANCO[aberto.pipeline_status].rotulo}
                    </button>
                  )}
                  {aberto.pipeline_status === 'active' && !aberto.tem_acesso && (
                    <button
                      onClick={() => mover('active')}
                      disabled={busy}
                      className="flex items-center justify-center gap-2 py-3.5 rounded-xl bg-blue-600 font-semibold disabled:opacity-50"
                    >
                      {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <KeyRound className="w-5 h-5" />}
                      Criar acesso do profissional
                    </button>
                  )}
                  {!['rejected', 'active'].includes(aberto.pipeline_status) && (
                    <div className="flex gap-2">
                      <button onClick={() => setAccao('returned')}
                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-800 text-amber-400 font-medium">
                        <Undo2 className="w-4 h-4" /> Devolver
                      </button>
                      <button onClick={() => setAccao('rejected')}
                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-800 text-red-400 font-medium">
                        <Ban className="w-4 h-4" /> Rejeitar
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationsPipeline;
